import { useRef, useState } from "react";
import { GRID_START as GRID_START0, GRID_END as GRID_END0, TONE, OFF_HOURS_BG, fmtH, hourOf, sameDay } from "@/lib/taskCalendar";

const snap = (x) => Math.round(x * 4) / 4;
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));

/**
 * Mřížka týdne / dne (7–19 h) s tahy jako v Google kalendáři (z návrhu Design, 191):
 *  - tah po prázdném místě  -> onRange(dayIndex, fromH, toH)
 *  - tah blokem (moveKey)    -> onMoveTo(moveKey, dayIndex, startH)
 *  - tah spodní hranou        -> onResizeTo(moveKey, endH)
 *  - klik do prázdna          -> onSlot(dayIndex, h)
 * Nad mřížkou je řádek „během dne“ (cols[i].allDay) pro úkoly bez času a vícedenní práci.
 * start/end: rozsah hodin (Úkoly 7–19, Docházka podle směn — 246).
 */
export function TimeGrid({ cols, rowH = 48, height = 640, colMin = 110, now = new Date(), days = [],
  onRange, onMoveTo, onResizeTo, onSlot, testid = "time-grid", allDayMax = 4, start: GRID_START = GRID_START0, end: GRID_END = GRID_END0 }) {
  const HOURS = Array.from({ length: GRID_END - GRID_START }, (_, i) => `${GRID_START + i}:00`);
  const colRefs = useRef([]);
  const drag = useRef(null);
  const suppress = useRef(false);
  const [preview, setPreview] = useState(null);
  const [tip, setTip] = useState(null);

  const hourAt = (ci, y) => { const el = colRefs.current[ci]; if (!el) return GRID_START; return GRID_START + (y - el.getBoundingClientRect().top) / rowH; };
  const colAt = (x) => { let best = 0, bd = 1e9; colRefs.current.forEach((el, i) => { if (!el) return; const r = el.getBoundingClientRect(); const d = x < r.left ? r.left - x : x > r.right ? x - r.right : 0; if (d < bd) { bd = d; best = i; } }); return best; };

  const begin = (e, d) => {
    if (e.button !== 0) return;
    // Na dotyku vyhrává posouvání stránky — tahy jsou jen pro myš.
    if (e.pointerType === "touch") return;
    e.preventDefault();
    drag.current = { ...d, x0: e.clientX, y0: e.clientY, moved: false };
    const mv = (ev) => {
      const s = drag.current; if (!s) return;
      if (!s.moved && Math.hypot(ev.clientX - s.x0, ev.clientY - s.y0) < 5) return;
      s.moved = true;
      if (s.mode === "create") { const h = clamp(snap(hourAt(s.ci, ev.clientY)), GRID_START, GRID_END); s.a = Math.min(s.h0, h); s.b = Math.max(s.h0 + 0.25, h); }
      if (s.mode === "move") { s.ci2 = colAt(ev.clientX); s.a = clamp(snap(hourAt(s.ci2, ev.clientY) - s.off), GRID_START, GRID_END - s.dur); s.b = s.a + s.dur; }
      if (s.mode === "resize") s.b = clamp(snap(hourAt(s.ci, ev.clientY)), s.a + 0.25, GRID_END);
      setPreview({ ci: s.mode === "move" ? s.ci2 : s.ci, a: s.a, b: s.b, key: s.key, mode: s.mode });
    };
    const up = (ev) => {
      window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up);
      const s = drag.current; drag.current = null; setPreview(null);
      if (!s || !s.moved || ev.type === "pointercancel") return;
      suppress.current = true; setTimeout(() => { suppress.current = false; }, 0);
      if (s.mode === "create") onRange?.(s.ci, s.a, s.b);
      if (s.mode === "move") onMoveTo?.(s.key, s.ci2, s.a);
      if (s.mode === "resize") onResizeTo?.(s.key, s.b);
    };
    window.addEventListener("pointermove", mv); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", up);
  };

  const gridH = rowH * HOURS.length;
  const top = (h) => (h - GRID_START) * rowH;
  const hasAllDay = cols.some((c) => c.allDay?.length);

  return (
    <div className="flex flex-col border border-border rounded-[14px] overflow-hidden bg-card" style={{ height }} data-testid={testid}>
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="sticky top-0 z-[6] min-w-full w-max bg-secondary/60 border-b border-border">
          <div className="flex">
            <div className="w-[46px] shrink-0 sticky left-0 bg-secondary z-[1]" />
            {cols.map((c, i) => (
              <div key={i} className="flex-1 basis-0 overflow-hidden px-1.5 py-2 text-center border-l border-border" style={{ minWidth: colMin, width: 0, contain: "inline-size" }}>
                <div className={`text-[11px] font-bold uppercase tracking-[.08em] truncate ${c.today ? "text-brand" : "text-muted-foreground"}`}>{c.label}</div>
                {c.sub && <div className="text-[11px] text-muted-foreground truncate mt-0.5">{c.sub}</div>}
                {!!c.tags?.length && (
                  <div className="flex flex-wrap justify-center gap-[3px] mt-1.5">
                    {c.tags.map((t) => (
                      <span key={t.key} className={`flex items-center gap-1 max-w-full h-5 px-[7px] rounded-full text-[11px] font-semibold whitespace-nowrap overflow-hidden text-ellipsis cursor-help ${TONE[t.tone]?.tag || ""}`}
                        title={t.tip} onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setTip({ text: t.tip, x: r.left + r.width / 2, y: r.bottom + 6 }); }}
                        onMouseLeave={() => setTip(null)} data-testid="grid-absence-tag">
                        {t.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          {hasAllDay && (
            <div className="flex border-t border-border bg-card" data-testid={`${testid}-allday`}>
              <div className="w-[46px] shrink-0 sticky left-0 bg-card z-[1] text-[9.5px] font-bold tracking-[.06em] text-muted-foreground text-right pr-1.5 pt-1.5 leading-tight">BĚHEM<br />DNE</div>
              {cols.map((c, i) => (
                <div key={i} className="flex-1 basis-0 min-w-0 border-l border-border p-1 flex flex-col gap-[3px]" style={{ minWidth: colMin, width: 0, contain: "inline-size" }}
                  onDragOver={c.onAllDayDrop ? (e) => e.preventDefault() : undefined} onDrop={c.onAllDayDrop ? (e) => { e.preventDefault(); c.onAllDayDrop(); } : undefined}>
                  {(c.allDay || []).slice(0, allDayMax).map((b) => (
                    <button key={b.key} type="button" onClick={b.onClick} title={b.tip || b.title} draggable={!!b.onDragStart} onDragStart={b.onDragStart}
                      className={`w-full text-left rounded-md border px-1.5 py-[2px] text-[11px] font-semibold truncate ${b.className || ""} ${b.done ? "line-through" : ""}`} data-testid={b.testid || "grid-allday"}>
                      {(b.dots || []).map((d) => <span key={d.key} className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: d.color }} />)}{b.title}
                      {b.sub && <span className="block text-[10.5px] font-medium opacity-85 truncate">{b.sub}</span>}
                    </button>
                  ))}
                  {(c.allDay || []).length > allDayMax && <span className="text-[10.5px] font-semibold text-muted-foreground px-1">+{c.allDay.length - allDayMax} další</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex relative min-w-full w-max">
          <div className="w-[46px] shrink-0 sticky left-0 bg-card z-[5]">
            {HOURS.map((h) => <div key={h} className="text-[10px] font-medium text-muted-foreground text-right pr-[7px] pt-[3px]" style={{ height: rowH }}>{h}</div>)}
          </div>
          {cols.map((c, ci) => (
            <div key={ci} ref={(el) => { colRefs.current[ci] = el; }}
              onPointerDown={onRange ? (e) => { if (e.target.closest("[data-blk]")) return; const h0 = Math.max(GRID_START, Math.floor(hourAt(ci, e.clientY) * 4) / 4); begin(e, { mode: "create", ci, h0, a: h0, b: h0 + 0.25 }); } : undefined}
              className="flex-1 basis-0 relative select-none border-l border-border"
              style={{ minWidth: colMin, height: gridH, backgroundImage: "linear-gradient(to bottom, rgba(127,127,127,.18) 0, rgba(127,127,127,.18) 1px, transparent 1px)", backgroundSize: `100% ${rowH}px` }}>
              {(c.off || []).map(([a, b], k) => <div key={k} className="absolute inset-x-0 pointer-events-none" style={{ top: top(a), height: (b - a) * rowH, background: OFF_HOURS_BG }} />)}
              {(c.absBlocks || []).map((ab) => ab.boxed ? (
                // 297: nepřítomnost jednoho z více lidí — blok v pruhu vedle úkolů (jako úkol), ne pás přes celý den
                <div key={ab.key} className={`absolute z-[1] pointer-events-none rounded-lg border-[1.5px] overflow-hidden px-1.5 py-[3px] ${TONE[ab.tone]?.tag || ""}`} data-testid="grid-absence"
                  style={{ top: top(ab.from) + 1, height: Math.max(20, (ab.to - ab.from) * rowH - 2), left: `calc(${((ab.lane || 0) * 100) / (ab.lanes || 1)}% + 2px)`, width: `calc(${100 / (ab.lanes || 1)}% - 4px)`,
                    background: TONE[ab.tone]?.hatch, borderColor: TONE[ab.tone]?.fg }}>
                  <div className="text-[11px] font-bold leading-tight truncate">{ab.label}</div>
                  <div className="text-[10.5px] font-semibold leading-tight opacity-85 break-words">{ab.time}</div>
                </div>
              ) : (
                <div key={ab.key} className="absolute inset-x-0 z-[1] pointer-events-none border-y-2" style={{ top: top(ab.from), height: (ab.to - ab.from) * rowH, background: TONE[ab.tone]?.hatch, borderColor: TONE[ab.tone]?.fg, color: TONE[ab.tone]?.fg }}>
                  <div className="px-2 py-1.5 text-[11.5px] font-bold leading-tight">{ab.label}<div className="font-semibold opacity-85">{ab.time}</div></div>
                </div>
              ))}
              {onSlot && Array.from({ length: (GRID_END - GRID_START) * 2 }, (_, i) => (
                <div key={i} className="absolute inset-x-0 z-[1] cursor-pointer hover:bg-brand/10" style={{ top: i * rowH / 2, height: rowH / 2 }}
                  onClick={() => { if (!suppress.current) onSlot(ci, GRID_START + i / 2); }} />
              ))}
              {(c.blocks || []).map((b) => {
                const n = b.lanes || 1, l = b.lane || 0;
                const dimmed = preview?.mode === "move" && preview.key === b.moveKey;
                return (
                  <div key={b.key} data-blk="1" title={b.tip || b.title}
                    onClick={(e) => { if (!suppress.current) b.onClick?.(e); }}
                    onPointerDown={b.moveKey ? (e) => { e.stopPropagation(); const dur = b.to - b.from; begin(e, { mode: "move", key: b.moveKey, ci, ci2: ci, dur, off: hourAt(ci, e.clientY) - b.from, a: b.from, b: b.to }); } : undefined}
                    className={`absolute rounded-lg px-1.5 py-[3px] overflow-hidden border text-left ${b.className || ""}`}
                    style={{ top: top(b.from) + 1, height: Math.max(20, (b.to - b.from) * rowH - 2), left: `calc(${(l * 100) / n}% + 2px)`, width: `calc(${100 / n}% - 4px)`, zIndex: b.z || 2, opacity: dimmed ? 0.35 : 1, cursor: b.moveKey ? "grab" : b.onClick ? "pointer" : "default" }}
                    data-testid="grid-block">
                    <div className={`text-[11px] font-semibold leading-tight truncate ${b.done ? "line-through" : ""}`}>{b.title}</div>
                    <div className="flex items-center gap-[3px] mt-px">
                      <span className="text-[10px] font-medium opacity-80 tabular-nums">{b.time}</span>
                      {(b.dots || []).map((d) => <span key={d.key} className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: d.color }} />)}
                    </div>
                    {b.resizable && (
                      <div title="Táhni pro změnu délky" className="absolute inset-x-0 bottom-0 h-2 cursor-ns-resize z-[2]"
                        onPointerDown={(e) => { e.stopPropagation(); begin(e, { mode: "resize", key: b.moveKey, ci, a: b.from, b: b.to }); }}>
                        <div className="w-[18px] h-[3px] mx-auto mt-[3px] rounded-sm bg-current opacity-35" />
                      </div>
                    )}
                  </div>
                );
              })}
              {preview && preview.ci === ci && (
                <div className="absolute inset-x-[3px] z-[8] pointer-events-none rounded-lg border-[1.5px] border-dashed border-brand bg-brand/15 text-brand text-[11px] font-bold px-1.5 py-[3px]"
                  style={{ top: top(preview.a) + 1, height: Math.max(16, (preview.b - preview.a) * rowH - 2) }}>
                  {fmtH(preview.a)}–{fmtH(preview.b)}
                </div>
              )}
              {days[ci] && sameDay(days[ci], now) && hourOf(now) >= GRID_START && hourOf(now) <= GRID_END && (
                <div className="absolute inset-x-0 h-0.5 bg-brand z-[4] pointer-events-none" style={{ top: top(hourOf(now)) }}>
                  <div className="absolute -left-1 -top-[3px] w-2 h-2 rounded-full bg-brand" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {tip && <div className="fixed z-[100] pointer-events-none -translate-x-1/2 px-3 py-[7px] rounded-[10px] bg-foreground text-background text-[12.5px] font-semibold whitespace-nowrap shadow-lg" style={{ left: tip.x, top: tip.y }}>{tip.text}</div>}
    </div>
  );
}
